import { useRef, useState, type KeyboardEvent } from "react";
import { ExternalLink } from "lucide-react";

import { cn } from "@/marketing/lib/utils";
import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";
import { CampaignOperationsPatches, type CampaignTrackerCellValue } from "./campaignOperationsPatches";
import { CampaignTrackerRows } from "./campaignTrackerRows";
import type { TrackerCellEditStart, TrackerCellSaveState } from "./trackerCellEdits";
import type { TrackerEditableColumn } from "./trackerColumns";

interface TrackerCellProps {
  campaign: InfluencerCampaign;
  column: TrackerEditableColumn;
  picOptions: string[];
  // Read when the editor opens: save state lives outside React, so a render-time copy can be stale.
  readSaveState: () => TrackerCellSaveState;
  onCommit: (campaign: InfluencerCampaign, field: string, value: CampaignTrackerCellValue, editStart: TrackerCellEditStart) => void;
}

const STATUS_TONE: Record<string, string> = {
  Completed: "tw-bg-emerald-50 tw-text-emerald-700",
  "Sent to Creator": "tw-bg-emerald-50 tw-text-emerald-700",
  "On Track": "tw-bg-emerald-50 tw-text-emerald-700",
  "At Risk": "tw-bg-amber-50 tw-text-amber-700",
  "Waiting: Brand Feedback": "tw-bg-amber-50 tw-text-amber-700",
  "Waiting: Tracking Number": "tw-bg-amber-50 tw-text-amber-700",
  "Need Revision": "tw-bg-amber-50 tw-text-amber-700",
  "Off Track": "tw-bg-red-50 tw-text-red-700",
  Paused: "tw-bg-slate-100 tw-text-slate-600",
  "Not Needed": "tw-bg-slate-100 tw-text-slate-500",
};

function toText(value: unknown): string {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return typeof value === "string" ? value : "";
}

/** One editable master-sheet cell: click to edit, Enter or leaving the field saves, Escape cancels. */
export function TrackerCell({ campaign, column, picOptions, readSaveState, onCommit }: TrackerCellProps) {
  const stored = CampaignOperationsPatches.read(campaign.operations, column.field);
  const storedText = toText(stored);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(storedText);
  // Closing is synchronous so the blur a browser fires on the closing input cannot save a second time or after Escape.
  const editorOpen = useRef(false);
  const editStart = useRef<TrackerCellEditStart>({ value: null, generation: 0 });

  const lastChange = CampaignTrackerRows.lastChange(campaign, column.field);
  const title = lastChange ? `Last changed by ${lastChange.by ?? "unknown"} · ${lastChange.at.slice(0, 16).replace("T", " ")}` : undefined;

  const startEditing = () => {
    setDraft(storedText);
    // A pending save means the shown value is this client's unsaved edit, not a server value.
    const saveState = readSaveState();
    editStart.current = { value: saveState.hasPendingSave ? undefined : stored ?? null, generation: saveState.generation };
    editorOpen.current = true;
    setEditing(true);
  };

  const closeEditor = (): boolean => {
    if (!editorOpen.current) return false;
    editorOpen.current = false;
    setEditing(false);
    return true;
  };

  const commitText = (text: string) => {
    if (!closeEditor()) return;
    if (text.trim() === storedText.trim()) return;
    onCommit(campaign, column.field, text, editStart.current);
  };

  const commitChoice = (choice: string) => {
    if (!closeEditor()) return;
    if (choice === storedText) return;
    if (column.editor === "boolean") {
      onCommit(campaign, column.field, choice === "" ? null : choice === "Yes", editStart.current);
      return;
    }
    onCommit(campaign, column.field, choice === "" ? null : choice, editStart.current);
  };

  const onTextKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Escape") {
      closeEditor();
      return;
    }
    const submits = column.editor === "longText" ? event.key === "Enter" && (event.metaKey || event.ctrlKey) : event.key === "Enter";
    if (submits) commitText(event.currentTarget.value);
  };

  const editorClass = "tw-w-full tw-rounded tw-border tw-border-primary tw-bg-background tw-px-1.5 tw-py-1 tw-text-xs tw-outline-none";

  const renderEditor = () => {
    switch (column.editor) {
      case "status":
      case "boolean": {
        const options = column.editor === "boolean" ? ["Yes", "No"] : (column.options ?? []);
        return (
          <select aria-label={column.header} autoFocus className={editorClass} value={draft} onChange={(event) => commitChoice(event.target.value)} onBlur={() => closeEditor()}>
            <option value="">—</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      }
      case "date":
        return (
          <input
            aria-label={column.header}
            autoFocus
            type="date"
            className={editorClass}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={(event) => commitText(event.target.value)}
            onKeyDown={onTextKeyDown}
          />
        );
      case "longText":
        return (
          <textarea
            aria-label={column.header}
            autoFocus
            rows={3}
            className={editorClass}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={(event) => commitText(event.target.value)}
            onKeyDown={onTextKeyDown}
          />
        );
      default: {
        const listId = column.editor === "pic" ? `tracker-pics-${column.id}` : undefined;
        return (
          <>
            <input
              aria-label={column.header}
              autoFocus
              list={listId}
              className={editorClass}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={(event) => commitText(event.target.value)}
              onKeyDown={onTextKeyDown}
            />
            {listId && (
              <datalist id={listId}>
                {picOptions.map((pic) => (
                  <option key={pic} value={pic} />
                ))}
              </datalist>
            )}
          </>
        );
      }
    }
  };

  const isHttpLink = column.editor === "link" && /^https?:\/\//i.test(storedText);

  return (
    <td className="tw-border-b tw-border-r tw-border-border tw-px-1.5 tw-py-1 tw-align-top">
      {editing ? (
        renderEditor()
      ) : (
        <div className="tw-flex tw-items-start tw-gap-1">
          <button
            type="button"
            aria-label={`${column.header}: ${storedText || "empty"}`}
            title={title}
            onClick={startEditing}
            className={cn(
              "tw-min-h-[22px] tw-w-full tw-truncate tw-rounded tw-px-1 tw-py-0.5 tw-text-left tw-text-xs hover:tw-bg-muted",
              column.editor === "status" && storedText ? STATUS_TONE[storedText] ?? "tw-bg-slate-50 tw-text-slate-700" : "",
              !storedText && "tw-text-muted-foreground/50",
            )}
          >
            {storedText || "—"}
          </button>
          {isHttpLink && (
            <a href={storedText} target="_blank" rel="noreferrer" aria-label={`Open ${column.header}`} className="tw-mt-1 tw-shrink-0 tw-text-muted-foreground hover:tw-text-foreground">
              <ExternalLink className="tw-h-3 tw-w-3" />
            </a>
          )}
        </div>
      )}
    </td>
  );
}
